import ContactLink from "@/components/ContactLink";
import { Link, useForm } from "@inertiajs/react";
import Callout from "@/components/help/Callout";
import Screenshot from "@/components/help/Screenshot";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import FormField from "@/components/forms/FormField";
import MoneyField from "@/components/forms/MoneyField";
import CampaignWindowFields from "@/components/schools/CampaignWindowFields";
import { keyDatesFor } from "@/lib/campaignPlanning";
import { WINDOW_DEFAULTS } from "@/lib/campaignWindows";

function NewCampaignFormPreview() {
  const { data, setData } = useForm({
    name: "Spring Read-a-thon 2026",
    start_at: "2026-04-13",
    end_at: "2026-04-24",
    money_goal: "5000",
    minute_goal: "50000",
    ...WINDOW_DEFAULTS,
  });
  const dates = keyDatesFor(data.start_at, data.end_at, data, "2026-03-23");
  return (
    <Card>
      <CardHeader>
        <CardTitle>Plan your read-a-thon</CardTitle>
        <CardDescription>Everything here stays editable until the campaign starts.</CardDescription>
      </CardHeader>
      <CardContent>
        <form className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField name="start_at" label="Reading starts" type="date" required value={data.start_at} onChange={(e) => setData("start_at", e.target.value)} />
            <FormField name="end_at" label="Last day of reading" type="date" required value={data.end_at} onChange={(e) => setData("end_at", e.target.value)} />
          </div>
          <CampaignWindowFields data={data} setData={setData} dates={data.end_at >= data.start_at ? dates : null} today="2026-03-23" />
          <FormField name="name" label="Campaign name" required value={data.name} onChange={(e) => setData("name", e.target.value)} />
          <div className="grid gap-4 sm:grid-cols-2">
            <MoneyField name="money_goal" label="Fundraising goal" value={data.money_goal} onChange={(e) => setData("money_goal", e.target.value)} />
            <FormField name="minute_goal" label="Reading goal (minutes)" type="number" value={data.minute_goal} onChange={(e) => setData("minute_goal", e.target.value)} />
          </div>
          <Button>Create campaign</Button>
        </form>
      </CardContent>
    </Card>
  );
}

export default function CreatingACampaign() {
  return (
    <>
      <p>
        A <strong>campaign</strong> is one read-a-thon. Pick the dates, set a goal if you'd like one, and you've got the shape of your event. Planning it is the second step of the setup guide, right after checking your school's details — and the rest of the guide (paying the campaign fee, Square, students, teachers, rewards, info sheets, take-home sheets) builds on it. You can run as many campaigns over the years as you want — fall, spring, every term — just not two at the same time. Each one has its own fee.
      </p>

      <h2>Before you start</h2>
      <p>
        Just have a rough idea of your start and end dates. If you gave us dates when you signed up, we'll fill them in; otherwise we'll suggest a Monday-to-Friday fortnight that leaves a week to set up and two full weeks of early donations. Once the campaign starts, those dates are locked in, so it's worth checking with whoever's running the read-a-thon before you commit. Everything else can be filled in later.
      </p>
      <p>
        Your first campaign is already waiting as a draft when you sign up, so you can pay its fee straight away. Its dates are only a placeholder until you save this step, and it can't go live before you do.
      </p>

      <h2>The form</h2>
      <Screenshot caption="What you'll fill in">
        <div className="max-w-md mx-auto">
          <NewCampaignFormPreview />
        </div>
      </Screenshot>

      <p>
        <strong>Name.</strong> This shows up everywhere families and donors look — receipts, donation pages, your dashboard. We suggest one to match the season, like "Spring Read-a-thon 2026", which works much better than "Q2 Fundraiser."
      </p>
      <p>
        <strong>Start and end dates.</strong> Students can read and log minutes between these dates. As you pick them, the form shows exactly what families will hear — when donations open, when logging closes, when donations close — and flags anything unusual, like a weekend start or too little time for early donations. The dates lock once the campaign starts, so pick carefully. (If you absolutely need to change them later, email <ContactLink>hello@pagepledge.com</ContactLink> and we'll help.)
      </p>
      <p>
        <strong>What it's for.</strong> One sentence about what you're raising money for. It's shown on donation pages, and you can add it to the info sheets' introduction.
      </p>
      <p>
        <strong>Goals.</strong> Optional, and you can change them any time before the campaign ends. A fundraising goal and a reading-minute goal both show up as progress bars across the school — they give students something to chase — and the setup guide sizes its suggested school-wide rewards to them. For the reading goal, the form works one out for you: your number of students × 20 minutes a day × the campaign's days, plus a stretch. We'll also email you at 25%, 50%, 75%, and 100% of goal so you can celebrate.
      </p>

      <Callout variant="note" title="Before and after reading">
        <p>
          Under the reading dates are three short sentences you can adjust. Donations open <em>any time</em> before reading starts by default: families can give as soon as they get their take-home sheet. Choose a number of days instead if you'd rather open them on a set date. Families can log minutes for 1 more day after reading ends (until 9 AM the next morning), or 0 to stop at the end of the last day. Donations stay open for 7 more days after that. Each shows the date it works out to, and a timeline underneath shows the whole campaign. Reading minutes still don't count until the start date.
        </p>
      </Callout>

      <Callout variant="tip" title="Pick a goal that feels like a stretch">
        <p>
          A goal that's just out of reach is more exciting than one you'll blow past in a week. Aim for something you'd be proud to hit but aren't sure you will. (You can always raise more than your goal — the bar isn't a cap.)
        </p>
      </Callout>

      <h2>What happens next</h2>
      <p>
        Once you save, the setup guide moves on to paying the campaign fee and Square, then students, teachers, rewards and getting the word out. On your start date, the campaign goes live on its own — there's no button to push. Reading minutes start counting, and teachers get an email so they know it's begun.
      </p>
      <Callout variant="note" title="Your campaign goes live once it's approved">
        <p>
          Your account starts as a free trial for setting up. Running a campaign costs a flat fee: the setup guide shows your price based on how many students you've added; pay by e-Transfer and we'll approve it. Until then it can't go live, connect Square, add teachers or email families. If your start date arrives first, it waits in draft. See <Link href="/help/articles/paying-for-your-campaign">Paying for your campaign</Link>.
        </p>
      </Callout>

      <h2>What's next</h2>
      <ul>
        <li><Link href="/help/articles/connecting-square">Connect Square</Link> — the next step in the setup guide.</li>
        <li><Link href="/help/articles/campaign-lifecycle">The four stages of a campaign</Link>.</li>
      </ul>
    </>
  );
}
